import React, { useState } from "react";
import { Button, Form, Input, Modal, message } from "antd";
import ApiService from "../API/apiService";
import { API_ENDPOINTS } from "../API/endpoints";

const PASSWORD_MIN = 6;
const PASSWORD_MAX = 64;

const ChangePasswordModal = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const close = () => {
    form.resetFields();
    onClose();
  };

  const save = async () => {
    if (saving) return;
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setSaving(true);
    try {
      await ApiService.put(
        API_ENDPOINTS.changePassword,
        { currentPassword: values.currentPassword, newPassword: values.newPassword },
        { skipCancelToken: true }
      );
      message.success("Password changed successfully");
      close();
    } catch (error) {
      message.error(error?.response?.data?.message || "Could not change the password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Change Password"
      open={open}
      onCancel={close}
      centered
      width={420}
      forceRender
      footer={[
        <Button key="cancel" className="btn-secondary" onClick={close}>Cancel</Button>,
        <Button key="save" type="primary" loading={saving} onClick={save}>Update Password</Button>,
      ]}
    >
      <Form form={form} layout="vertical" requiredMark={false} onFinish={save}>
        <Form.Item
          name="currentPassword"
          label="Current Password"
          rules={[{ required: true, message: "Enter your current password" }]}
        >
          <Input.Password placeholder="Current password" autoComplete="current-password" maxLength={PASSWORD_MAX} />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="New Password"
          dependencies={["currentPassword"]}
          rules={[
            { required: true, message: "Enter a new password" },
            { min: PASSWORD_MIN, max: PASSWORD_MAX, message: `Password must be ${PASSWORD_MIN} to ${PASSWORD_MAX} characters` },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                value && value === getFieldValue("currentPassword")
                  ? Promise.reject(new Error("New password must be different from the current password"))
                  : Promise.resolve(),
            }),
          ]}
        >
          <Input.Password placeholder="New password" autoComplete="new-password" maxLength={PASSWORD_MAX} />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm New Password"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "Confirm the new password" },
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || value === getFieldValue("newPassword")
                  ? Promise.resolve()
                  : Promise.reject(new Error("Passwords do not match")),
            }),
          ]}
        >
          <Input.Password placeholder="Re-enter new password" autoComplete="new-password" maxLength={PASSWORD_MAX} />
        </Form.Item>
        <button type="submit" hidden aria-hidden="true" />
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
